<template>
    <div class="person-fields">
        <h2 v-text="heading" />

        <p v-if="userIsAdmin === null" class="recruit-message">
            Become a new member of Acadia Senior College.<br>
            <span class="login-message">
                <img src="/img/icon-notice.jpg">
                Already a member? <a href="/login">Click to Login</a>
            </span>
        </p>

        <div class="notes">
            (*) Required fields
        </div>

        <form ref="myForm" method="post" accept-charset="utf-8" class="edit" :action="formAction">
            <input type="hidden" name="_method" :value="newPerson ? 'POST' : 'PUT'">

            <account-info
                :person="$v.myPerson"
                :user-is-admin="userIsAdmin"
                :new-person="newPerson"
            />
            <about-you
                ref="aboutYou"
                :person="$v.myPerson"
                :user-is-admin="userIsAdmin"
                :new-person="newPerson"
                :phone-number-types="phoneNumberTypes"
                @set-address-status="setAddressStatus"
                @validate="validateAddress"
            />
            <admin-panel
                :person="$v.myPerson"
                v-if="userIsAdmin"
            />

            <address-validation-modal
                ref="addressValidator"
                :person="$v.myPerson"
                @review="reviewAddress"
                @set-address-status="setAddressStatus"
                @accept="acceptAddress"
                @override="overrideAddress"
                @cancel="cancelSubmit"
            />
            <input
                type="hidden"
                name="address_status_id"
                v-model="myPerson.address_status_id"
            />

            <button
                @click.prevent="doSubmit"
                :disabled="$v.myPerson.$invalid || $v.myPerson.$anyError || ajax.pending"
                v-text="submitLabel"
            />

            <div v-if="userIsAdmin && personErrors.length > 0">
                <h3>Form errors</h3>
                <ul>
                    <li
                        v-for="([key, {value, errors}]) in personErrors"
                        :key="key"
                    >
                       {{ key }}
                        <ul>
                            <li
                                v-for="error in errors"
                                :key="error"
                            >
                                {{ error }}
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </form>
    </div>
</template>

<script>
import axios from 'axios';
import { validationMixin } from 'vuelidate';
import { v4 as uuidv4 } from 'uuid';

import LabelInput from '../../_components/form/LabelInput';
import AboutYou from './AboutYou';
import AccountInfo from './AccountInfo';
import AdminPanel from './AdminPanel';
import AddressValidationModal from './AddressValidationModal';

import { required, requiredIf, requiredUnless, email, minLength } from 'vuelidate/lib/validators'

const notEmpty = val => val !== '';

const requiredIfNew = function(val, person) {
    console.debug((this.newPerson), (val === ''));
    return !(this.newPerson && val === '');
}

const passwordsMatch = function(val, person) {
    axios.post('/api/js-logs', {
        message: 'Person form passwordsMatch.',
        context: {
            newPerson: this.newPerson,
            typeof_password_new: typeof person.password_new,
            typeof_password_confirm: typeof person.password_confirm,
            password_new: person.password_new,
            password_confirm: person.password_confirm,
            equal: person.password_new == person.password_confirm,
            equiv: person.password_new === person.password_confirm,
        },
    });

    let ref = this.newPerson ? person.password : person.password_new;
    return ref === person.password_confirm;
};

const requiredUnlessAdmin = function(val, person) {
    return this.userIsAdmin || val !== '';
};

const usernameIsValid = function(val) {
    return /^[a-z0-9][-_a-z0-9]*$/i.test(val);
};

const usernameIsUnique = function(username) {
    if (username === '' || username === this.person.username || !this.$v.myPerson.username.usernameIsValid) {
        return true;
    }

    return axios.get('/api/username-validation', {
        params: {
            username: this.myPerson.username,
        },
    })
        .then(response => {
            return !!response.data?.isUnique;
        })
};

const nonzeroUnlessDeleted = (val, item) => {
    return item.deleted || Number(val) > 0;
};


const emailIsUnique = function(email) {
    if (email === '' || email === this.person.email || !this.$v.myPerson.email.required || !this.$v.myPerson.email.email) {
        return true;
    }

    return axios.get('/api/email-validation', {
        params: {
            email: this.myPerson.email,
        },
    })
        .then(response => {
            return !!response.data?.isUnique;
        })
};

const atLeastOneNotDeleted = function(arr) {
    if (!Array.isArray(arr)) {
        return false;
    }
    return arr.filter(item => !item.deleted).length > 0;
};

export default {
    components: {
        AboutYou,
        AccountInfo,
        AdminPanel,
        LabelInput,
        AddressValidationModal,
    },

    mixins: [
        validationMixin,
    ],

    props: {
        person: {
            type: Object,
        },
        userIsAdmin: {
            type: Boolean,
        },
        newPerson: {
            type: Boolean,
        },
        phoneNumberTypes: {
            type: Array,
        },
    },

    data() {
        return {
            myPerson: {},
            ajax: {
                pending: false,
            },
        };
    },

    validations: {
        myPerson: {
            username: { required, usernameIsValid, usernameIsUnique },
            password: { requiredIfNew },
            password_new: {},
            password_confirm: { passwordsMatch },
            email: { required, email, emailIsUnique },
            newsletter: {},
            isAdmin: {},
            activated: {},
            deleted: {},
            nameFirst: { required },
            nameLast: { required },
            address1: { requiredUnlessAdmin },
            address2: {},
            city: { requiredUnlessAdmin },
            state: { requiredUnlessAdmin },
            zip: { requiredUnlessAdmin },
            address_status_id: {},
            phone_numbers: {
                minLength: atLeastOneNotDeleted,
                $each: {
                    phone_number_type_id: { required: nonzeroUnlessDeleted },
                    phone_number: { required: requiredUnless('deleted') },
                    key: {},
                    deleted: {},
                },
            },
            notes: {},
            student: {
                member_through: { required: requiredIf(student => userIsAdmin && !(student && student.deleted)) },
                deleted: {},
                justAdded: {},
            },
            instructor: {
                descLong: {},
                active: {},
                deleted: {},
                justAdded: {},
            },
            credit: {},
        },
    },

    computed: {
        formAction() {
            return window.location.pathname;
        },

        heading() {
            if (this.userIsAdmin) {
                return (this.newPerson ? 'Add' : 'Edit') + ' person';
            }
            return this.newPerson ? 'Join' : 'Your account';
        },

        submitLabel() {
            return submitLabel ?? 'Save changes';
        },

        address() {
            return {
                address1: this.person.address1,
                address2: this.myPerson.address2,
                city: this.myPerson.city,
                state: this.myPerson.state,
                zip: this.myPerson.zip,
            };
        },

        personErrors() {
            return Object.keys(this.$v.myPerson)
                .filter(key => !key.startsWith('$') && this.$v.myPerson[key].$invalid && !this.$v.myPerson[key].$pending)
                // .map(key => ([key, this.$v.myPerson[key].$model]))
                .map(key => {
                    const errors = Object.keys(this.$v.myPerson[key])
                        .filter(prop => !prop.startsWith('$') && this.$v.myPerson[key][prop] === false)
                    ;
                    return [key, {
                        value: this.$v.myPerson[key].$model,
                        errors,
                    }];
                })
            ;
        },
    },

    watch: {
        '$v.myPerson': {
            deep: true,
            handler() {
                if (!this.userIsAdmin) {
                    return;
                }

                if (this.personErrors.length > 0) {
                    axios.post('/api/js-logs', {
                        message: 'Person form has invalid fields.',
                        context: Object.fromEntries(this.personErrors),
                    });
                }
            },
        },
        address: {
            deep: true,
            handler() {
                this.myPerson.address_status_id = 1;
            },
        },
    },

    methods: {
        reviewAddress(fields) {
            this.$refs.aboutYou.reviewAddress(fields);
        },

        acceptAddress(address) {
            this.myPerson = Object.assign(this.myPerson, address);
            this.myPerson.address_status_id = 2;
            this.$nextTick()
                .then(() => {
                    if (!this.userIsAdmin) {
                        this.$refs.myForm.submit();
                    }
                })
            ;
        },

        overrideAddress(address) {
            this.myPerson.address_status_id = this.userIsAdmin ? 5 : 4;
            this.$nextTick()
                .then(() => {
                    if (!this.userIsAdmin) {
                        this.$refs.myForm.submit();
                    }
                })
            ;
        },

        cancelSubmit() {
            console.debug('cancelSubmit');
        },

        setAddressStatus(status) {
            this.myPerson.address_status_id = status;
        },

        doSubmit() {
            this.$refs.aboutYou.clearReview();
            this.ajax.pending = true;
            if (this.myPerson.address_status_id === 1) {
                this.$refs.addressValidator.validate()
                    .then(submit => {
                        if (submit) {
                            this.$refs.myForm.submit();
                        } else {
                            console.debug('no submit');
                        }
                    })
                    .finally(() => {
                        this.ajax.pending = false;
                    })
                ;
            } else {
                this.$refs.myForm.submit();
            }
        },

        validateAddress() {
            this.$refs.addressValidator.validate();
        },
    },

    created() {
        let person = JSON.parse(JSON.stringify(this.person));

        if (this.newPerson) {
            person.password = '';
            person.phone_numbers.push({
                id: null,
                key: uuidv4(),
                phone_number: '',
                phone_number_type_id: 1,
                deleted: false,
            });
        } else {
            person.password_new = '';
        }
        person.password_confirm = '';

        person.phone_numbers.forEach(phone => {
            Object.assign(phone, {key: phone.id, deleted: false});
        });
        if (person.student) {
            person.student.justAdded = false;
            person.student.member_through = person.student.member_through.substring(0, 10);
        }

        this.myPerson = Object.assign({}, person);
        this.$nextTick()
            .then(() => {
                this.$v.$reset();
            })
        ;
    },
}
</script>

<style lang="scss">
//@import '~bulma/bulma.sass';
@import '~bulma/sass/utilities/_all.sass';
@import '~bulma/sass/form/_all.sass';

.person-fields {
    *:not(i) {
        font-family: sans-serif;
        color: black;
    }

    .recruit-message {
        margin-top: 5px;
        font-size: 1.3rem;
    }

    .login-message {
        font-size: 90%;
        margin: 10px 0 0 0;
        display: inline-block;
        font-style: italic;
        
        img {
            height: 1em;
            margin: 0 10px -2px 5px;
        }
    }

    fieldset {
        border: 1px solid #c0c0c0;
        margin: 0 2px 2rem 0;
        padding: 10px 0 5px 0;
    }

    legend {
        font-size: 1.5rem;
        text-decoration: none;
        padding: 0 0.5em;
    }

    label.label, input.input, textarea.textarea {
        font-size: 1.2rem;
        font-weight: normal;
        line-height: 1;
        border-color: #8f8f8f;
        height: auto;
    }

    label.label {
        width: 9em !important;
        display: inline-block;
        vertical-align: top;
        font-size: 1.2rem;
        padding-top: 0;
        text-align: left;
    }

    input[type=checkbox] {
        width: auto !important;
    }

    input.input, textarea.textarea {
        padding: 4px;
        border-radius: 0;
        width: 20em !important;
        min-width: unset;
    }

    .select select {
        font-size: 1.2rem !important;
        line-height: 1;
        margin: 0;
        height: auto;
        padding-top: 5px;
        padding-bottom: 5px;
        padding-left: 4px;
    }

    .select:not(.is-multiple):not(.is-loading)::after {
        right: 0.5em;
        margin-top: -0.875em;
    }

    .field:not(:last-child) {
        margin-bottom: 0;
    }
}
</style>
